import Button from "common/Buttons/Button.tsx";
import { CSSProperties, useCallback, useState } from "react";
import CreateBotModal from "common/Home/Header/CreateBotModal.tsx";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import { Plus } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import styled, { css } from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BotType, LanguageNameSpaces } from "@shared/types";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import { getCreateBotRoute } from "utils/routes.tsx";
import { useHistory } from "react-router-dom";
import { useCanManageBotType } from "@shared/features/bots/hooks.ts";
import { CREATE_BOT_BUTTON_ID } from "testIds.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface CreateBotButtonProps {
  style?: CSSProperties;
}

const CreateBotButton = ({ style }: CreateBotButtonProps) => {
  const [openModal, setOpenModal] = useState(false);
  const isSmallScreen = useMedia([`(max-width: 1000px)`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const history = useHistory();
  const { isSubUser } = useSubUserPermissions();
  const canCreateSignalsBot = useCanManageBotType(BotType.SIGNAL);

  const closeModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const onButtonClick = () => {
    if (isSubUser && canCreateSignalsBot) {
      history.push(getCreateBotRoute(BotType.SIGNAL));
    } else if (!isSubUser) {
      setOpenModal(true);
    }
  };

  return (
    <>
      <StyledButton
        data-testid={CREATE_BOT_BUTTON_ID}
        $type="secondary"
        $isSmallScreen={isSmallScreen}
        onClick={onButtonClick}
        style={style}
      >
        <LookIcon
          classic={<PlusIcon />}
          newLook={<Plus size={16} strokeWidth={2} />}
        />
        {!isSmallScreen && t("create_bot_modal.create_bot_button")}
      </StyledButton>
      <CreateBotModal open={openModal} closeModal={closeModal} />
    </>
  );
};

export default CreateBotButton;

const StyledButton = styled(Button)<{ $isSmallScreen: boolean }>`
  margin: 0 12px;
  height: 34px;
  ${(p) =>
    p.$isSmallScreen &&
    `
        width: 30px;
        height: 30px;
        padding: 0;
        margin-right: 6px;
    `};
  @media (max-width: ${phoneMaxWidth}) {
    width: 26px;
    height: 26px;
    ${(p) =>
      p.theme.isNewLook &&
      css`
        /* Same 36px as the bell beside it; the hit area pads out to 44. */
        &&& {
          position: relative;
          width: 36px;
          height: 36px;
          margin: 0 4px 0 0;
          border-radius: 10px;
          box-shadow: none;
        }
        &::before {
          content: "";
          position: absolute;
          inset: -4px;
        }
      `}
  }
`;
const PlusIcon = styled(PlusOutlined)`
  font-size: 12px;
`;
